import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ReviewPhoto {
  id: string | number;
  src: string;
  label: string;
  category: string;
}

interface ReviewPhotosProps {
  photos: ReviewPhoto[];
  totalCount: number;
}

export default function ReviewPhotos({
  photos = [],
  totalCount = 0,
}: ReviewPhotosProps) {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('전체');

  const categories = useMemo(() => {
    const map = new Map<string, number>();

    photos.forEach((photo) => {
      const category = photo.category || '기타';
      map.set(category, (map.get(category) || 0) + 1);
    });

    return [
      {
        categoryName: '전체',
        count: photos.length,
      },
      ...Array.from(map.entries()).map(([categoryName, count]) => ({
        categoryName,
        count,
      })),
    ];
  }, [photos]);

  const filtered = activeCategory === '전체'
    ? photos.slice(0, 10)
    : photos.filter((photo) => photo.category === activeCategory).slice(0, 10);

  return (
    <div id="photos" className="pt-8">
      <h2 className="text-lg md:text-xl font-bold text-[#121B35]">
        실제 후기 사진
      </h2>

      <p className="text-xs md:text-sm text-[#536079] mt-1">
        다이렉트 후기에서 확인된 사진만 모아봤어요.
      </p>

      <div className="sticky top-14 lg:top-16 z-30 bg-[#F7F2EA]">
        <div className="max-w-[1280px] mx-auto pt-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {categories.map((cat) => (
              <button
                key={cat.categoryName}
                onClick={() => setActiveCategory(cat.categoryName)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${
                  activeCategory === cat.categoryName
                    ? 'bg-[#121B35] text-white'
                    : 'bg-white text-[#536079] border border-[#E8E2DA]'
                }`}
              >
                {cat.categoryName}
                <span
                  className={`ml-1 ${
                    activeCategory === cat.categoryName
                      ? 'text-[#B9965B]'
                      : 'text-[#9AA3B2]'
                  }`}
                >
                  {cat.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        className="grid grid-cols-3 lg:grid-cols-5 gap-1.5 lg:gap-2 mt-4"
        style={{ minHeight: '320px' }}
      >
        {filtered.map((photo, index) => (
          <div
            key={photo.id}
            className={`relative aspect-square rounded-md overflow-hidden cursor-pointer ${
              index >= 9 ? 'hidden lg:block' : ''
            }`}
            onClick={() => navigate(`/yangbok-v5/test/photos?photo=${photo.id}`)}
          >
            <img
              src={photo.src}
              alt={photo.label}
              className="w-full h-full object-cover"
            />

            <span className="absolute bottom-1 left-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">
              {photo.label}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-4 lg:flex lg:justify-end">
        <button
          className="w-full lg:w-auto lg:px-10 border border-[#121B35] text-[#121B35] rounded-lg py-3 text-sm md:text-base font-medium whitespace-nowrap"
          onClick={() => navigate('photos')}
        >
          전체 후기 사진 {totalCount}장 보기 →
        </button>
      </div>
    </div>
  );
}